import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Plus, Package2, Layers, ChevronRight, ChevronDown, CheckCircle2, Wrench, FileEdit, Star, Pencil, ShoppingBasket, AlertTriangle } from 'lucide-react';
import { useAdminReleases, useMarkAsCurrentRelease, type LibraryRelease } from '@/hooks/useLibraryReleases';
import NewReleaseDialog from './NewReleaseDialog';
import EditBundleCompositionDialog from './EditBundleCompositionDialog';

const BRAND_LABELS: Record<string, string> = {
  saint_gobain: 'Saint-Gobain',
  knauf: 'Knauf',
  fassa: 'Fassa Bortolo',
};

const CONTENT_LABELS: Record<string, string> = {
  materials: 'Listino',
  stratigraphies: 'Stratigrafie',
  bundle: 'Bundle',
};

export default function AdminReleases() {
  const { data, isLoading } = useAdminReleases();
  const [showNew, setShowNew] = useState(false);
  const [editingBundle, setEditingBundle] = useState<LibraryRelease | null>(null);
  const [expandedBundles, setExpandedBundles] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const markCurrent = useMarkAsCurrentRelease();

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;

  const releases = data ?? [];
  // Indice per slug → release (necessario per espandere includes_release_slugs nei bundle)
  const slugMap = new Map(releases.map((r) => [r.slug, r]));
  // Raggruppa per brand
  const byBrand = releases.reduce<Record<string, LibraryRelease[]>>((acc, r) => {
    const k = r.brand_slug ?? 'altro';
    (acc[k] ??= []).push(r);
    return acc;
  }, {});

  const toggleExpand = (id: string) => {
    setExpandedBundles((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Release</h1>
          <p className="text-sm text-zinc-500">Pacchetti vendibili nello Store. Una release contiene N materiali e/o stratigrafie. Una stessa entità può vivere in più release (es. Fassa 2026 + 2027).</p>
        </div>
        <Button onClick={() => setShowNew(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Nuova release
        </Button>
      </div>
      <NewReleaseDialog open={showNew} onOpenChange={setShowNew} />

      {Object.entries(byBrand).map(([brand, items]) => (
        <section key={brand}>
          <h2 className="text-sm font-semibold text-zinc-700 mb-2 uppercase tracking-wider">
            {BRAND_LABELS[brand] ?? brand} ({items.length})
          </h2>
          <div className="space-y-2">
            {items.map((r) => {
              const isBundle = r.content_type === 'bundle';
              const composedSlugs = r.includes_release_slugs ?? [];
              const composedReleases = composedSlugs.map((s) => slugMap.get(s)).filter(Boolean) as LibraryRelease[];
              const expanded = expandedBundles.has(r.id);
              const orphanSlugs = composedSlugs.filter((s) => !slugMap.has(s));
              return (
                <Card
                  key={r.id}
                  className={`hover:border-purple-300 transition-colors ${isBundle ? 'border-emerald-200' : 'cursor-pointer'}`}
                  onClick={isBundle ? undefined : () => navigate(`/admin/releases/${r.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {isBundle && <ShoppingBasket className="h-4 w-4 text-emerald-600 shrink-0" />}
                          <span className="font-semibold text-zinc-900">{r.name}</span>
                          <Badge variant="outline" className="text-[10px] bg-zinc-50">v{r.version_year}{r.version_suffix ?? ''}</Badge>
                          <Badge variant="outline" className={`text-[10px] ${isBundle ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                            {CONTENT_LABELS[r.content_type] ?? r.content_type}
                          </Badge>
                          {r.status === 'published' ? (
                            <Badge className="text-[10px] bg-emerald-600 hover:bg-emerald-600"><CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />Pubblicata</Badge>
                          ) : r.status === 'draft' ? (
                            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200"><FileEdit className="h-2.5 w-2.5 mr-0.5" />Bozza</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] bg-zinc-50">{r.status}</Badge>
                          )}
                          {r.is_current_year && <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">Corrente</Badge>}
                        </div>
                        <div className="text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
                          <span className="font-mono text-[10px]">{r.slug}</span>
                          {isBundle ? (
                            <span className="flex items-center gap-1">
                              <ShoppingBasket className="h-3 w-3" />
                              {composedSlugs.length === 0 ? (
                                <span className="text-amber-700 font-medium">⚠ Bundle vuoto</span>
                              ) : (
                                <>Include <strong>{composedSlugs.length}</strong> release</>
                              )}
                            </span>
                          ) : (
                            <>
                              <span className="flex items-center gap-1"><Package2 className="h-3 w-3" />{r.materials_count} materiali</span>
                              <span className="flex items-center gap-1"><Layers className="h-3 w-3" />{r.stratigraphies_count} strat</span>
                            </>
                          )}
                          <span>€{Number(r.price_eur).toFixed(0)}</span>
                          {r.lemonsqueezy_variant_id ? (
                            <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">LS configurato</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px] bg-zinc-50"><Wrench className="h-2.5 w-2.5 mr-0.5" />LS da collegare</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!r.is_current_year && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-purple-200 text-purple-700 hover:bg-purple-50"
                            disabled={markCurrent.isPending}
                            title={`Imposta come versione corrente della famiglia ${BRAND_LABELS[r.brand_slug ?? ''] ?? r.brand_slug ?? ''} ${CONTENT_LABELS[r.content_type] ?? r.content_type}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Marcare "${r.name}" come Corrente? Le altre versioni della stessa famiglia (${CONTENT_LABELS[r.content_type] ?? r.content_type} ${BRAND_LABELS[r.brand_slug ?? ''] ?? r.brand_slug ?? ''}) verranno automaticamente de-marcate.`)) {
                                markCurrent.mutate(r.id);
                              }
                            }}
                          >
                            <Star className="h-3 w-3 mr-1" />
                            Marca come corrente
                          </Button>
                        )}
                        {isBundle ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={(e) => { e.stopPropagation(); setEditingBundle(r); }}
                            >
                              <Pencil className="h-3 w-3 mr-1" />
                              Modifica composizione
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={(e) => { e.stopPropagation(); toggleExpand(r.id); }}
                              title={expanded ? 'Nascondi release incluse' : 'Mostra release incluse'}
                            >
                              {expanded ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRight className="h-4 w-4 text-zinc-500" />}
                            </Button>
                          </>
                        ) : (
                          <ChevronRight className="h-5 w-5 text-zinc-400" />
                        )}
                      </div>
                    </div>

                    {/* Sezione espansa per i bundle: lista delle release incluse */}
                    {isBundle && expanded && (
                      <div className="mt-3 ml-6 pt-3 border-t border-zinc-100 space-y-1">
                        {composedReleases.length === 0 && orphanSlugs.length === 0 && (
                          <div className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Bundle vuoto: nessuna release puntata. Clicca "Modifica composizione" per popolarlo.
                          </div>
                        )}
                        {composedReleases.map((cr) => (
                          <div key={cr.slug} className="flex items-center gap-2 text-xs text-zinc-700">
                            {cr.content_type === 'materials' ? (
                              <Package2 className="h-3 w-3 text-blue-600" />
                            ) : cr.content_type === 'stratigraphies' ? (
                              <Layers className="h-3 w-3 text-purple-600" />
                            ) : (
                              <ShoppingBasket className="h-3 w-3 text-emerald-600" />
                            )}
                            <span>{cr.name}</span>
                            <Badge variant="outline" className="text-[9px] bg-zinc-50">v{cr.version_year}{cr.version_suffix ?? ''}</Badge>
                            {cr.is_current_year && <Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-700 border-purple-200">Corrente</Badge>}
                            <span className="font-mono text-[10px] text-zinc-400 ml-auto">{cr.slug}</span>
                          </div>
                        ))}
                        {orphanSlugs.map((s) => (
                          <div key={s} className="text-xs text-red-700 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Slug puntato ma release non trovata: <span className="font-mono">{s}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      ))}

      {releases.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Package2 className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
            <h3 className="font-semibold text-zinc-900 mb-1">Nessuna release ancora</h3>
            <p className="text-sm text-zinc-500 mb-4">Crea la tua prima release per iniziare a vendere prodotti nello Store.</p>
            <Button onClick={() => setShowNew(true)}><Plus className="h-4 w-4 mr-1" />Nuova release</Button>
          </CardContent>
        </Card>
      )}

      {editingBundle && (
        <EditBundleCompositionDialog
          bundle={editingBundle}
          allReleases={releases}
          open={true}
          onOpenChange={(o) => { if (!o) setEditingBundle(null); }}
        />
      )}
    </div>
  );
}
